import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { UIExportModal } from "../../../../../ui";
import messages from "../../../../../../assets/locale/messages";
import { isSuperAdmin } from "../../../../../../utils/Permissions";
import { isSaudiNationality } from "../../../../../../containers/Company-portal/Applicants/ViewApplicantProfile/profileHelpers";

const EXPORT_FIELD_KEYS = [
  "name",
  "id",
  "days",
  "payrollDays",
  "autoDeduction",
  "manualDeduction",
  "manualDeductionReason",
  "manualDeductionAmount",
  "reward",
  "rewardReason",
  "numberOfTrainingDays",
  "trainingPayPerDay",
  "totalTrainingPay",
  "totalAmount",
  "stcNumber",
  "iqamaId",
  "training",
  "transferCommission",
  "signature",
  "role",
];

const EventPayrollExportModal = ({ open, data, isSingle, onClose, onConfirm }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { payroll, shared } = messages[lang];
  const showStcNumber = isSuperAdmin();

  const isNationalIdLabel = isSingle
    ? isSaudiNationality(data?.applicant?.nationality)
    : (data?.applicants || []).length > 0 &&
      data.applicants.every((applicant) => isSaudiNationality(applicant.nationality));

  const fieldKeys = useMemo(
    () =>
      EXPORT_FIELD_KEYS.filter((key) => key !== "stcNumber" || showStcNumber),
    [showStcNumber],
  );

  const fields = useMemo(() => {
    const labels = payroll.payrollApplicants;
    return fieldKeys.map((key) => {
      if (key === "totalAmount") return { name: key, label: labels.labels.totalAmount };
      if (key === "iqamaId") {
        return { name: key, label: isNationalIdLabel ? labels.nationalId : labels.iqamaId };
      }
      return { name: key, label: labels[key] };
    });
  }, [fieldKeys, payroll.payrollApplicants, isNationalIdLabel]);

  const [selectedFields, setSelectedFields] = useState({});

  useEffect(() => {
    if (!open) return;
    setSelectedFields(Object.fromEntries(fieldKeys.map((key) => [key, false])));
  }, [open, fieldKeys]);

  const handleFieldChange = (name, checked) => {
    setSelectedFields((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSelectAll = (checked) => {
    setSelectedFields(
      Object.fromEntries(fieldKeys.map((key) => [key, checked])),
    );
  };

  const selectedCount = fieldKeys.filter((key) => selectedFields[key]).length;
  const isAllSelected =
    fieldKeys.length > 0 && selectedCount === fieldKeys.length;
  const isIndeterminate = selectedCount > 0 && !isAllSelected;

  return (
    <UIExportModal
      open={open}
      className="event-payroll-generate__export-modal"
      fieldsColumns={4}
      title={payroll.payrollApplicants.exportPayroll}
      description={payroll.payrollApplicants.exportPayrollSubtitle}
      selectAllLabel={payroll.payrollApplicants.selectAll}
      cancelLabel={shared.cancel}
      confirmLabel={shared.confirm}
      fields={fields}
      selectedFields={selectedFields}
      isAllSelected={isAllSelected}
      isIndeterminate={isIndeterminate}
      confirmDisabled={selectedCount === 0}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={() => onConfirm?.(selectedFields, fields)}
      onFieldChange={handleFieldChange}
      onSelectAll={handleSelectAll}
      maxWidth="xl"
    />
  );
};

EventPayrollExportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  data: PropTypes.object,
  isSingle: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
};

EventPayrollExportModal.defaultProps = {
  data: null,
  isSingle: false,
  onConfirm: undefined,
};

export default EventPayrollExportModal;
